import { isEmpty } from 'lodash';
import * as moment from 'moment';
import { Moment } from 'moment';

const dateTimeISOFormat = 'YYYY-MM-DDTHH:mm:ss';
const dateFormat = 'YYYY-MM-DD';
export const visibleDateTimeFormat = 'DD-MM-YYYY HH:mm:ss';
export const visibleDateTimeFormatPipe = 'dd-MM-yyyy HH:mm:ss';
const visibleDateFormat = 'DD-MM-YYYY';
const timeFormat = 'HH:mm';
export const timeWithSecondsFormat = 'HH:mm:ss';
const dayMonthTimeFormat = 'DD-MM HH:mm:ss';
export const fileNameDateTimeFormat = 'YYYYMMDDHHmmss';

export const dateTimeISORegEx = new RegExp(/(\d{4})\-(\d{2})\-(\d{2})(T|\s)(\d{2}):(\d{2}):(\d{2})/);

export function toDateTimeIsoFormat(date: Moment): string {
  return date.format(dateTimeISOFormat);
}

export function toDateFormat(date: Moment | Date): string {
  if (date instanceof Date)
    date = moment(date);
  return date.format(dateFormat);
}

// Ścieżka bez moment dla Date - moment.format parsuje wzorzec formatu przy każdym wywołaniu
// i jest o rząd wielkości wolniejszy, a w tabelach z wieloma kolumnami dat robi się z tego
// kilkaset wywołań na jedno dorenderowanie wierszy. Wynik identyczny z moment().format(...),
// łącznie z "Invalid date" dla niepoprawnych dat (stąd fallback).
export function toVisibleDateTimeFormat(date: Moment | Date): string {
  if (date instanceof Date) {
    if (isNaN(date.getTime()))
      return moment(date).format(visibleDateTimeFormat);
    return `${formatVisibleDate(date)} ${formatVisibleTime(date)}`;
  }
  return date.format(visibleDateTimeFormat);
}

export function toVisibleTimeFormat(date: Moment | Date): string {
  if (date instanceof Date) {
    if (isNaN(date.getTime()))
      return moment(date).format(timeWithSecondsFormat);
    return formatVisibleTime(date);
  }
  return date.format(timeWithSecondsFormat);
}

export function toVisibleDayMonthTimeFormat(date: Moment | Date): string {
  if (date instanceof Date) {
    if (isNaN(date.getTime()))
      return moment(date).format(dayMonthTimeFormat);
    return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)} ${formatVisibleTime(date)}`;
  }
  return date.format(dayMonthTimeFormat);
}

export function stringToVisibleDateTimeFormat(dateString: string): string {
  return moment(dateString).format(visibleDateTimeFormat);
}

export function toVisibleDateFormat(date: Moment | Date): string {
  if (date instanceof Date) {
    if (isNaN(date.getTime()))
      return moment(date).format(visibleDateFormat);
    return formatVisibleDate(date);
  }
  return date.format(visibleDateFormat);
}

function formatVisibleDate(date: Date): string {
  return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${String(date.getFullYear()).padStart(4, '0')}`;
}

function formatVisibleTime(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

export function toTime(date: Moment, seconds: boolean): string {
  if (seconds)
    return date.format(timeWithSecondsFormat);
  else
    return date.format(timeFormat);
}

export function toUnixTimestamp(value: any): number {
  return +moment(value).format('x');
}

export function toFileNameDateTimeFormat(date: Moment | Date): string {
  if (date instanceof Date)
    date = moment(date);
  return date.format(fileNameDateTimeFormat);
}


declare module 'moment' {
  export interface Moment {
    toDateTimeIsoFormat(): string;
    toDateFormat(): string;
    toVisibleDateTimeFormat(): string;
    toVisibleDateFormat(): string;
    toTime(seconds: boolean): string;
  }
}

(moment.fn as any).toDateTimeIsoFormat = function(): string {
  const self = this as moment.Moment;
  return toDateTimeIsoFormat(self);
};

(moment.fn as any).toDateFormat = function(): string {
  const self = this as moment.Moment;
  return toDateFormat(self);
};

(moment.fn as any).toVisibleDateTimeFormat = function(): string {
  const self = this as moment.Moment;
  return toVisibleDateTimeFormat(self);
};

(moment.fn as any).toVisibleDateFormat = function(): string {
  const self = this as moment.Moment;
  return toVisibleDateFormat(self);
};

(moment.fn as any).toTime = function(seconds: boolean): string {
  const self = this as moment.Moment;
  return toTime(self, seconds);
};

export function calculateMinutesBetweenNowAndDate(date: Date | null): number {
  if (date) {
    return moment(new Date()).diff(moment(date), 'minutes');
  } else {
    return 0;
  }
}

export function calculateDaysBetweenNowAndDate(date: Date | null): number {
  if (date) {
    return moment(new Date()).diff(moment(date), 'days');
  } else {
    return 0;
  }
}

export function formatDateByLocale(date: Date | null, locale: string): string | null {
  if (date == null || isEmpty(locale))
    return null;
  const format = (locale.toLocaleLowerCase() === 'pl') ? 'DD.MM.YYYY HH:mm:ss' : 'YYYY.MM.DD HH:mm:ss';
  return moment(date).format(format);
}

export function toTime36(dateTime: Date, baseDate: Date): string {
  const baseDay = Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  const targetDay = Date.UTC(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
  const dayDiff = Math.floor((targetDay - baseDay) / (1000 * 60 * 60 * 24));

  const target = moment(dateTime);
  const hours36 = target.hour() + dayDiff * 24;
  const minutes = target.minute();
  return `${hours36.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
